import { IDENTITY_ERROR_CODES } from '@identity/application/errors';
import type { IdentityErrorCode } from '@identity/application/errors';
import type { RequestAuthorizer, VerificationChannel } from '@identity/domain/types';
import type { AuthApiValidationErrors } from '@identity/infrastructure/api/types';

/**
 * Wire code to domain code. This is the one place in the repository where a prefixed code appears:
 * the domain keeps its own vocabulary, which is what the i18n keys and the screens are written in.
 *
 * Two prefixes reach the same duplicate address code, because two services reject it with their own.
 */
const API_CODE_TO_DOMAIN_CODE: Record<string, IdentityErrorCode> = {
  'auth.LinkInvalid': IDENTITY_ERROR_CODES.LINK_INVALID,
  'auth.LinkExpired': IDENTITY_ERROR_CODES.LINK_EXPIRED,
  'auth.LinkCompleted': IDENTITY_ERROR_CODES.LINK_COMPLETED,
  'auth.PasswordPolicy': IDENTITY_ERROR_CODES.PASSWORD_POLICY,
  'auth.CurrentPasswordRequired': IDENTITY_ERROR_CODES.CURRENT_PASSWORD_REQUIRED,
  'auth.CurrentPasswordInvalid': IDENTITY_ERROR_CODES.CURRENT_PASSWORD_INVALID,
  'auth.EmailInUse': IDENTITY_ERROR_CODES.EMAIL_IN_USE,
  'parrot.EmailAlreadyInUse': IDENTITY_ERROR_CODES.EMAIL_IN_USE,
  'auth.LinkSendRateLimited': IDENTITY_ERROR_CODES.LINK_SEND_RATE_LIMITED,
  'auth.RecoveryRateLimited': IDENTITY_ERROR_CODES.RECOVERY_RATE_LIMITED,
};

/**
 * The framework rejection for a request that carried no credential. It has no `auth.` prefix
 * because it is produced before the request reaches the view.
 */
const NOT_AUTHENTICATED_API_CODE = 'not_authenticated';

/**
 * What a missing credential means, per authorizer.
 *
 * With a link it is the credential the wizard runs on that is gone, and opening the link again is
 * the only way out of both causes the front end cannot tell apart. With a session it is the Parrot
 * session that died, and calling that an expired link would explain an object the user never saw.
 * The open endpoint asks for no credential, so it cannot produce this at all.
 */
const NOT_AUTHENTICATED_BY_AUTHORIZER: Record<RequestAuthorizer, IdentityErrorCode | undefined> = {
  link: IDENTITY_ERROR_CODES.LINK_EXPIRED,
  session: IDENTITY_ERROR_CODES.EXPIRED_TOKEN,
  none: undefined,
};

/** The wire code the backend reuses for a verification a later send replaced. */
const LINK_INVALID_API_CODE = 'auth.LinkInvalid';

/** What only the caller of a request knows, and what the ambiguous codes are read against. */
export type ApiErrorCodeContext = {
  /**
   * Whether the request asked about one verification. Only then does an invalid link mean that a
   * later send replaced it; everywhere else the same code is still a broken link.
   */
  isVerificationAnchored?: boolean;
};

/**
 * Translates the wire code into the domain code the screens are written against.
 *
 * An unknown code is not guessed: it returns `undefined`, the user gets the generic message and the
 * wire code stays whole in the log, which is where a backend sending something new is noticed.
 * `permission_denied` is deliberately among them — the scope table is fixed and the front end
 * already respects it, so a `403` is a routing bug of ours and its reader is the log.
 *
 * Two codes are ambiguous on the wire and are resolved by what the caller sent, not by the table: a
 * missing credential, which the authorizer settles, and an invalid link, which means a replaced
 * verification only for the status query that named one.
 */
export const mapApiErrorCode = (
  apiCode: string | undefined,
  authorizer: RequestAuthorizer,
  context: ApiErrorCodeContext = {},
): IdentityErrorCode | undefined => {
  if (!apiCode) return undefined;

  if (apiCode === NOT_AUTHENTICATED_API_CODE) return NOT_AUTHENTICATED_BY_AUTHORIZER[authorizer];

  if (apiCode === LINK_INVALID_API_CODE && context.isVerificationAnchored)
    return IDENTITY_ERROR_CODES.VERIFICATION_SUPERSEDED;

  return API_CODE_TO_DOMAIN_CODE[apiCode];
};

/** The fields of this domain a serializer can reject, one per endpoint that sends user input. */
export type ValidationField = 'email' | 'password' | 'value';

/** Code the serializer uses for a value that is already taken by another account. */
const UNIQUE_VALIDATION_CODE = 'unique';

export type ValidationErrorContext = {
  validation?: AuthApiValidationErrors | null;
  /** The field this call sent. Only the caller knows which one it was. */
  field: ValidationField;
  /** The channel of a link send, which is what decides the message of the generic `value` field. */
  channel?: VerificationChannel;
};

/** The domain code of a rejected `value`, which is an address on one channel and a number on the rest. */
const linkSendValueCode = (codes: string[], channel?: VerificationChannel): IdentityErrorCode => {
  if (channel !== 'EMAIL') return IDENTITY_ERROR_CODES.PHONE_INVALID;

  return codes.includes(UNIQUE_VALIDATION_CODE)
    ? IDENTITY_ERROR_CODES.EMAIL_IN_USE
    : IDENTITY_ERROR_CODES.EMAIL_INVALID;
};

/**
 * Resolves the domain code of a serializer rejection from the field the caller just sent.
 *
 * It cannot live any deeper than the repository: `value` is a generic field name, and an address
 * already registered and a badly written phone number are the same rejection until someone says
 * which channel the request was for.
 */
export const mapValidationErrorCode = ({
  validation,
  field,
  channel,
}: ValidationErrorContext): IdentityErrorCode | undefined => {
  const codes = validation?.[field]?.map((entry) => entry.code) ?? [];

  if (codes.length === 0) return undefined;

  if (field === 'email') return IDENTITY_ERROR_CODES.EMAIL_INVALID;

  // Tolerated alias, not the expected path: the policy arrives as a business code with no
  // validation map, and this only keeps both shapes landing on the same message.
  if (field === 'password') return IDENTITY_ERROR_CODES.PASSWORD_POLICY;

  return linkSendValueCode(codes, channel);
};
